import React, { FC, useEffect } from 'react'
import styled from 'styled-components'
import { BtnPlay, ScrollHWrapper } from 'components/base-ui'
import { MediaTypeEnum, getTypeImageFromExtension } from 'helpers/image'
import { BrandTypeEnum } from 'brands'
import media from 'styles/media'
import { fancyBoxReady } from 'helpers/fancybox'
import { ProductQuery, ListProductsQuery } from 'graphQL/dinein/generate/operations'

interface GalleryProps {
  brandType: string
  galleries: ProductQuery['product']['images'][] | ListProductsQuery['listProducts']['products'][number]['images'][]
  logoImage?: string
}

const Gallery: FC<GalleryProps> = ({ brandType, galleries, logoImage }) => {
  useEffect(() => {
    if (galleries) {
      fancyBoxReady(() => {
        window.$('[data-fancybox="gallery"]').fancybox({
          loop: true,
        })
      })
    }
  }, [galleries])

  return (
    <SlideWrapper>
      <ScrollHWrapper>
        <GalleryRow>
          {galleries.map((images, colIndex) => {
            return (
              <GalleryCol key={colIndex}>
                {images.map((image, imageIndex) => {
                  const type = getTypeImageFromExtension(image.resizableURL)
                  const isVideoType = type === MediaTypeEnum.VIDEO ? true : false
                  let _imagePath = ''
                  let _videoPath = ''
                  if (isVideoType) {
                    _imagePath = image.thumbnailResizableURL
                      .replace('{width}', '750')
                      .replace(
                        '{height}',
                        `750_fmt-jpg${
                          brandType === BrandTypeEnum.THOUSAND_PRODUCTS ||
                          brandType === BrandTypeEnum.MALL ||
                          brandType === BrandTypeEnum.MALL_WITHOUT_GROCERY
                            ? '_fit-contain'
                            : ''
                        }`
                      )
                    _videoPath = image.originalURL
                  } else {
                    _imagePath = image.resizableURL
                      .replace('{width}', '750')
                      .replace(
                        '{height}',
                        `750_fmt-jpg${
                          brandType === BrandTypeEnum.THOUSAND_PRODUCTS ||
                          brandType === BrandTypeEnum.MALL ||
                          brandType === BrandTypeEnum.MALL_WITHOUT_GROCERY
                            ? '_fit-contain'
                            : ''
                        }`
                      )
                  }
                  return (
                    <GalleryItem
                      data-fancybox
                      key={imageIndex}
                      xlg={colIndex === 0 && images.length === 1 && galleries.length === 1}
                      lg={colIndex === 0}
                      video={isVideoType}
                      href={`${isVideoType ? _videoPath : image.originalURL}`}
                      data-type={isVideoType ? 'video' : 'image'}
                      style={{
                        backgroundImage: `url('${_imagePath}')`,
                      }}
                    >
                      {isVideoType && (
                        <BtnPlayWrapper>
                          <BtnPlay className="btn-play" />
                        </BtnPlayWrapper>
                      )}
                    </GalleryItem>
                  )
                })}

                {colIndex !== 0 && images.length === 1 && logoImage && (
                  <GalleryItem
                    style={{
                      backgroundImage: `url('${logoImage}`,
                    }}
                  />
                )}
              </GalleryCol>
            )
          })}

          {galleries.length === 0 && (
            <GalleryCol>
              <GalleryItem
                xlg
                style={{
                  backgroundImage: `url('${process.env.PUBLIC_URL}/assets/placeholder-product-img.png`,
                }}
              />
            </GalleryCol>
          )}
        </GalleryRow>
      </ScrollHWrapper>
    </SlideWrapper>
  )
}

export default Gallery
const SlideWrapper = styled.div`
  position: relative;
  width: calc(100% + 40px) !important;
  margin-left: -${({ theme }) => theme.spacing['3XL']};
  margin-bottom: ${({ theme }) => theme.spacing['3XS']};
  padding-bottom: ${({ theme }) => theme.spacing.L};
`

const GalleryRow = styled.div`
  white-space: nowrap;
  padding-left: ${({ theme }) => theme.spacing['3XL']};
`

const GalleryCol = styled.div`
  display: inline-block;
  margin-right: 15px;
  vertical-align: top;

  &:last-child {
    margin-right: ${({ theme }) => theme.spacing['3XL']}px;
  }
`

const GalleryItem = styled.div<{ lg?: boolean; xlg?: boolean; video?: boolean; href?: string }>`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.XL};
  width: calc(37.8vw - 20px);
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  background-color: white;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (${media.screenMdMin}) {
    width: ${(414 * 37.8) / 100 - 20}px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${({ lg }) =>
    lg &&
    `
    margin-bottom: 0;
    width: calc(58.3vw - 20px);

    @media (${media.screenMdMin}) {
      width: ${(414 * 58.3) / 100 - 20}px;
    }
    `}

  ${({ xlg }) =>
    xlg &&
    `
    margin-bottom: 0;
    width: calc(100vw - 40px);

    @media (${media.screenMdMin}) {
      width: ${414 - 40}px;
    }
    `}

  ${({ video }) =>
    video &&
    `
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: black;
      opacity: 0.1;
    }
    `}
`

const BtnPlayWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`
